import   React                          ,
       { useState                       ,
	     useEffect                      ,
		 useRef                         }  from 'react';
import   classNames                        from 'classnames';
import   styles                            from './ProductSheet_ProductVariant.module.scss';

import   Field                             from '../../widget/inputs/Field';
import   Toggle                            from '../../widget/inputs/Toggle';
import   RichTextEditor                    from '../../widget/inputs/RichTextEditor';
import   WidgetFrame                       from '../../widget/navigation/WidgetFrame';
import   Trigger                           from '../../widget/actuators/Trigger';
import   CollapsiblePanel                  from '../../widget/windows/CollapsiblePanel';
import   CardManager , { Item }            from '../../widget/navigation/CardSet';

import   ProductSheet_Characteristic    ,
       { ErrorMessagesForCharacteristic }  from './ProductSheet_Characteristic';
import   ProductSheet_Price                from './ProductSheet_Price';
import   ProductSheet_Media             ,
       { ErrorMessagesForMedia          }  from './ProductSheet_Media';

import { ProductVariant                 ,
         ProductCharacteristic          ,
         ProductMedia                   ,
         ProductPrice                   ,
		 initializedCharacteristic      ,
		 initializedMedia               ,
		 initializedPrice               }  from '../../../dataTypes/productManagment/types';

import { ProductSheetConfig             }  from './ProductSheetConfig';
import { WWW_URLS                       }  from '../../../backendServices/urls';
import { createPaypalPlanForVariant     }  from '../../../backendServices/productManagment/api_product';

export interface ErrorMessagesForVariant {
    code            : string,
    name            : string,
	medias          : ErrorMessagesForMedia[]
	characteristics : ErrorMessagesForCharacteristic[]
}

export interface ProductSheet_ProductVariant_Props {
  productVariant       : ProductVariant;
  config               : ProductSheetConfig;
  errorMessages        : ErrorMessagesForVariant[];
  indexInErrorMessages : number;
  title                : string;
  publicRef?           : string;
  width?               : string;
  onChange?            : (newProductVariant: ProductVariant) => void;
  onDelete?            : () => void;
  onSetThumbnail?      : (mediaId: string | number) => void; // ✅ AJOUTER
  open?                : boolean;
  isEditMode           : boolean;
}

const ProductSheet_ProductVariant = ({ 
  productVariant,
  config,
  errorMessages,
  indexInErrorMessages,
  title,
  publicRef,
  width = "100%",
  onChange,
  onDelete,
  onSetThumbnail, // ✅ AJOUTER
  open,
  isEditMode 
}: ProductSheet_ProductVariant_Props) => {

  // ============================================================================
  // Extraction de la config
  // ============================================================================
  const { fieldsConfig, characteristicTypeOptionsConfig } = config;

  const initialProductVariant = productVariant;

  const [fields, setFields] = useState({
    id              : initialProductVariant.id                    ,
    name            : initialProductVariant.name            ?? "" ,
    complement      : initialProductVariant.complement      ?? "" ,
    description     : initialProductVariant.description     ?? "" ,
    code            : initialProductVariant.code            ?? "" ,
    barcode         : initialProductVariant.barcode         ?? "" ,
    active          : initialProductVariant.active                ,
    stockAvailable  : initialProductVariant.stockAvailable  ?? 0  ,
    stockBooked     : initialProductVariant.stockBooked     ?? 0  ,
    stockAssigned   : initialProductVariant.stockAssigned   ?? 0  ,
    stockThreshold  : initialProductVariant.stockThreshold  ?? 0  ,
    creationDate    : initialProductVariant.creationDate          ,
    characteristics : initialProductVariant.characteristics       ,
    medias          : initialProductVariant.medias                ,
    prices          : initialProductVariant.prices                ,
  });
  
  const [messages, setMessages] = useState({
    name : errorMessages?.[indexInErrorMessages]?.name ?? '',
    code : errorMessages?.[indexInErrorMessages]?.code ?? '',
  });

  // Récurrence (abonnement) : PAS dans `fields` — ce n'est pas édité ici, seulement
  // affiché, et le plan Paypal est créé par un appel immédiat (pas par le bouton
  // Enregistrer de la fiche produit). Cf. handleCreatePaypalPlan.
  const [ externalPaypalPlanId , setExternalPaypalPlanId ] = useState(initialProductVariant.externalPaypalPlanId ?? null);
  const [ creatingPaypalPlan   , setCreatingPaypalPlan   ] = useState(false);
  const [ paypalPlanError      , setPaypalPlanError      ] = useState('');
  
  const [, setOpenCharacteristics] = useState(() => {
    const initialOpenCharacteristics: Record<number, boolean> = {};
    fields.characteristics.forEach((_, index) => {
      initialOpenCharacteristics[index] = true;
    });
    return initialOpenCharacteristics;
  });

  const [, setOpenMedias] = useState(() => {
    const initialOpenMedias: Record<number, boolean> = {};
    fields.medias.forEach((_, index) => {
      initialOpenMedias[index] = true;
    });
    return initialOpenMedias;
  });

  const [openPrices, setOpenPrices] = useState(() => {
    const initialOpenPrices: Record<number, boolean> = {};
    fields.prices.forEach((_, index) => {
      initialOpenPrices[index] = true;
    });
    return initialOpenPrices;
  });

  const [ errorMessagesForCharacteristics , setErrorMessagesForCharacteristics ] = useState<ErrorMessagesForCharacteristic[]>([])
  const [ errorMessagesForMedias          , setErrorMessagesForMedias          ] = useState<ErrorMessagesForMedia[]>([])

  let defautURL = `${WWW_URLS.customer_front}/produit?id=${fields.id}`;

  // ============================================================================
  // useEffect
  // ============================================================================

  useEffect(() => {
    if (productVariant) {
      setFields({
        id              : productVariant.id,
        name            : productVariant.name            ?? "" ,
        complement      : productVariant.complement      ?? "" ,
        description     : productVariant.description     ?? "" ,
        code            : productVariant.code            ?? "" ,
        barcode         : productVariant.barcode         ?? "" ,
        active          : productVariant.active                ,
        stockAvailable  : productVariant.stockAvailable  ?? 0  ,
        stockBooked     : productVariant.stockBooked     ?? 0  ,
        stockAssigned   : productVariant.stockAssigned   ?? 0  ,
        stockThreshold  : productVariant.stockThreshold  ?? 0  ,
        creationDate    : productVariant.creationDate          ,
        characteristics : productVariant.characteristics       ,
        medias          : productVariant.medias                ,
        prices          : productVariant.prices                ,
      });
    }
  }, [productVariant]);

  useEffect(() => {
    if (isEditMode && productVariant) {
      setFields((prev) => {
        const updatedPrices = prev.prices.map((price) =>
          price.id ? price : { ...price, id: Date.now() + Math.random() }
        );

        return { ...prev, prices: updatedPrices };
      });
    }
  }, []);

  useEffect(() => {
    setMessages({
      name : errorMessages?.[indexInErrorMessages]?.name ?? '',
      code : errorMessages?.[indexInErrorMessages]?.code ?? '',
    });
  }, [errorMessages]);

  // ============================================================================
  // Handlers généraux
  // ============================================================================

  const notifyVariantChange = (newVariant: ProductVariant) => {
    if (onChange) {
      onChange(newVariant);
    }
  };

  const handleActiveChange = (newValue: boolean) => {
    setFields((prev) => {
      const updatedFields = { ...prev, active: newValue };
      notifyVariantChange(updatedFields);
      return updatedFields;
    });
  };

  const handleCreatePaypalPlan = async () => {
    setCreatingPaypalPlan(true);
    setPaypalPlanError('');

    const response = await createPaypalPlanForVariant(fields.id);

    if (response.code === 1) {
      setExternalPaypalPlanId(response.payload);
    } else {
      setPaypalPlanError(response.comment || "Impossible de créer le plan Paypal.");
    }

    setCreatingPaypalPlan(false);
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFields((prev) => {
      const updatedFields = { ...prev, code: newValue };
      notifyVariantChange(updatedFields);
      return updatedFields;
    });
    setMessages((prev) => ({ ...prev, code: newValue === '' ? 'Il manque la référence variant' : '' }));
  };

  const handleBarcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFields((prev) => {
      const updatedFields = { ...prev, barcode: newValue };
      notifyVariantChange(updatedFields);
      return updatedFields;
    });
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFields((prev) => {
      const updatedFields = { ...prev, name: newValue };
      notifyVariantChange(updatedFields);
      return updatedFields;
    });
    setMessages((prev) => ({ ...prev, name: newValue === '' ? 'Il manque le nom' : '' }));
  };

  const handleComplementChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFields((prev) => {
      const updatedFields = { ...prev, complement: newValue };
      notifyVariantChange(updatedFields);
      return updatedFields;
    });
  };

  const handleDescriptionChange = (newValue: string) => {
    setFields((prev) => {
      const updatedFields = { ...prev, description: newValue };
      notifyVariantChange(updatedFields);
      return updatedFields;
    });
  };

  const handleStockAvailableChange = (e: React.ChangeEvent<HTMLInputElement>, parsedValue: string | number | File | Date) => {
    setFields((prev) => {
      const updatedFields = { ...prev, stockAvailable: parsedValue as number };
      notifyVariantChange(updatedFields);
      return updatedFields;
    });
  };

  const handleStockBookedChange = (e: React.ChangeEvent<HTMLInputElement>, parsedValue: string | number | File | Date) => {
    setFields((prev) => {
      const updatedFields = { ...prev, stockBooked: parsedValue as number };
      notifyVariantChange(updatedFields);
      return updatedFields;
    });
  };

  const handleStockAssignedChange = (e: React.ChangeEvent<HTMLInputElement>, parsedValue: string | number | File | Date) => {
    setFields((prev) => {
      const updatedFields = { ...prev, stockAssigned: parsedValue as number };
      notifyVariantChange(updatedFields);
      return updatedFields;
    });
  };

  const handleStockThresholdChange = (e: React.ChangeEvent<HTMLInputElement>, parsedValue: string | number | File | Date) => {
    setFields((prev) => {
      const updatedFields = { ...prev, stockThreshold: parsedValue as number };
      notifyVariantChange(updatedFields);
      return updatedFields;
    });
  };

  // ============================================================================
  // Gestion des prix
  // ============================================================================

  const handlePriceChange = (updatedPrice: ProductPrice, priceIndex: number) => {
    setFields((prev) => {
      const updatedPrices = [...prev.prices];
      updatedPrices[priceIndex] = updatedPrice;
      const updatedFields = { ...prev, prices: updatedPrices };
      notifyVariantChange(updatedFields);
      return updatedFields;
    });
  };

  const handleAddPrice = () => {
    setFields((prev) => {
      const newPrice = {
        ...initializedPrice(),
        open: true,
        id: Date.now() + Math.random()
      };
      
      const updatedPrices = [...prev.prices, newPrice];
      const updatedFields = { ...prev, prices: updatedPrices };
      setOpenPrices((prev) => ({ ...prev, [fields.prices.length]: true }));
      notifyVariantChange(updatedFields);
      return updatedFields;
    });
  };

  const handleDeletePrice = (index: number) => {
    setFields((prev) => {
      const updatedPrices = prev.prices.filter((_, i) => i !== index);
      const updatedFields = { ...prev, prices: updatedPrices };
      notifyVariantChange(updatedFields);
      return updatedFields;
    });
  };

  // ============================================================================
  // Gestion des caractéristiques
  // ============================================================================

  const characteristicRefsMap = useRef<Map<string, React.RefObject<HTMLInputElement>>>(new Map());

  const getOrCreateCharacteristicRef = (charId: string | number) => {
    const key = `${charId}`;
    if (!characteristicRefsMap.current.has(key)) {
      const newRef = React.createRef<HTMLInputElement>();
      characteristicRefsMap.current.set(key, newRef);
    }
    return characteristicRefsMap.current.get(key)!;
  };

/*
  const renderCharacteristicField = () => {
    const visibleCharacteristics = fields.characteristics
      .filter(char => char.tag !== 'to delete')
      .sort((a, b) => a.rank - b.rank);
    
    const cardItems: Item[] = visibleCharacteristics.map((char, index) => {
      const charId = `${char.id}`;
      getOrCreateCharacteristicRef(charId);
      
      return {
        id: charId,
        component: (
          <ProductSheet_Characteristic 
            defaultValue={defautURL}
            characteristic={char}
            errorMessages={errorMessagesForCharacteristics}
            indexInErrorMessages={index}
            title={`Caractéristique n°${index + 1}`}
            onChange={(p) => handleCharacteristicChange(p, index)}
            withCollapsiblePanel={false}
            characteristicTypeOptionsConfig={characteristicTypeOptionsConfig}
          />
        ),
      };
    });

    return (
      <CardManager 
        containerWidth="100%" 
        containerHeight="237px" 
        cardWidth={700} 
        cardHeight={200}
        gap={20}
        showRemove={true}
        showUpload={false}
        showLibrary={false}
        onAdd={handleAddCharacteristicFromCard} 
        onRemove={handleDeleteCharacteristicFromCard}
        onReorder={handleCharacteristicReorder}
        items={cardItems}
      />
    );
  };
*/

const renderCharacteristicField = () => {
  const visibleCharacteristics = fields.characteristics
    .filter(char => char.tag !== 'to delete')
    .sort((a, b) => a.rank - b.rank);
  
  const cardItems: Item[] = visibleCharacteristics.map((char, index) => {
    const charId = `${char.id}`;
    getOrCreateCharacteristicRef(charId);
    
    // Vérifier si la caractéristique a une erreur
    const config = char.kind ? characteristicTypeOptionsConfig[char.kind] : null;  /////////////////////////////
    const hasNameError = config?.requiresName && (!char.name || char.name.trim() === '');
    const hasValueError = !char.value || char.value.trim() === '';
    const hasError = hasNameError || hasValueError;
    
    return {
      id: charId,
      component: (
        <ProductSheet_Characteristic 
          defaultValue={defautURL}
          characteristic={char}
          errorMessages={errorMessagesForCharacteristics}
          indexInErrorMessages={index}
          title={`Caractéristique n°${index + 1}`}
          onChange={(p) => handleCharacteristicChange(p, index)}
          withCollapsiblePanel={false}
          characteristicTypeOptionsConfig={characteristicTypeOptionsConfig}
          hasError={hasError} // ← AJOUTER CETTE PROP
        />
      ),
    };
  });

  return (
    <CardManager 
      containerWidth="100%" 
      containerHeight="237px" 
      cardWidth={700} 
      cardHeight={200}
      gap={20}
      showRemove={true}
      showUpload={false}
      showLibrary={false}
      onAdd={handleAddCharacteristicFromCard} 
      onRemove={handleDeleteCharacteristicFromCard}
      onReorder={handleCharacteristicReorder}
      items={cardItems}
    />
  );
};


  const handleCharacteristicReorder = (reorderedIds: string[]) => {
    setFields(prev => {
      const charMap = new Map(fields.characteristics.map(char => [`${char.id}`, char]));
      
      const reorderedCharacteristics = reorderedIds
        .map(id => charMap.get(id))
        .filter((char): char is ProductCharacteristic => char !== undefined)
        .map((char, newIndex) => {
          const newRank = newIndex + 1;
          const hasRankChanged = char.rank !== newRank;
          
          return {
            ...char,
            rank: newRank,
            tag: hasRankChanged && char.tag !== 'to create' && char.tag !== 'to delete' 
              ? 'to update' 
              : char.tag
          };
        });

      return { ...prev, characteristics: reorderedCharacteristics };
    });
  };

/*
  const handleAddCharacteristicFromCard = () => {
    const newCharId = `char_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    const currentChars = fields.characteristics;
    const maxRank = currentChars.length > 0 ? Math.max(...currentChars.map(c => c.rank)) : 0;
    const newRank = maxRank + 1;
    
    const newChar = {
      ...initializedCharacteristic(),
      open: true,
      id: newCharId,
      rank: newRank,
      tag: 'to create'
    };

    setFields(prev => {
      return { ...prev, characteristics: [...fields.characteristics, newChar] };
    });

    setOpenCharacteristics(prev => ({ ...prev, [fields.characteristics.length]: true }));
  };
*/

const handleAddCharacteristicFromCard = () => {
  // Vérifier si toutes les caractéristiques existantes sont valides
  if (!checkAllCharacteristicsValid()) {
    console.warn('⚠️ Impossible d\'ajouter une caractéristique variant : certaines caractéristiques existantes sont mal remplies');
    return; // Bloquer l'ajout
  }

  const newCharId = `char_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  const currentChars = fields.characteristics;
  const maxRank = currentChars.length > 0 ? Math.max(...currentChars.map(c => c.rank)) : 0;
  const newRank = maxRank + 1;
  
  const newChar = {
    ...initializedCharacteristic(),
    open: true,
    id: newCharId,
    rank: newRank,
    tag: 'to create'
  };

  setFields(prev => {
    return { ...prev, characteristics: [...fields.characteristics, newChar] };
  });

  setOpenCharacteristics(prev => ({ ...prev, [fields.characteristics.length]: true }));
};

  const handleDeleteCharacteristicFromCard = (key: string) => {
    setFields((prev) => {
      return {
        ...prev,
        characteristics: fields.characteristics.map((char) => {
          if (`${char.id}` === key) {
            if (char.tag === 'to create') return null;
            return { ...char, tag: 'to delete' };
          }
          return char;
        }).filter((char): char is ProductCharacteristic => char !== null),
      };
    });

    characteristicRefsMap.current.delete(key);
  };

  const handleCharacteristicChange = (updatedCharacteristic: ProductCharacteristic, charIndex: number) => {
    setFields((prev) => {
      const updatedCharacteristics = [...fields.characteristics];
      const originalChar = updatedCharacteristics[charIndex];
      
      const hasChanged = 
        originalChar.name !== updatedCharacteristic.name ||
        originalChar.value !== updatedCharacteristic.value ||
        originalChar.kind !== updatedCharacteristic.kind ||
        originalChar.unit !== updatedCharacteristic.unit;
      
      const newTag = hasChanged && originalChar.tag !== 'to create' && originalChar.tag !== 'to delete'
        ? 'to update'
        : originalChar.tag || updatedCharacteristic.tag;

      updatedCharacteristics[charIndex] = { ...updatedCharacteristic, tag: newTag };
      
      const updatedFields = { ...prev, characteristics: updatedCharacteristics };
      notifyVariantChange(updatedFields);
      return updatedFields;
    });

    setErrorMessagesForCharacteristics((prev) => {
      const updatedErrors = [...prev];
      const nameError = !updatedCharacteristic.name?.trim() ? 'Il manque le nom' : '';
      const valueError = !updatedCharacteristic.value?.trim() ? 'Il manque la valeur' : '';
      updatedErrors[charIndex] = { name: nameError, value: valueError };
      return updatedErrors;
    });
  };

const checkAllCharacteristicsValid = (): boolean => {
  return fields.characteristics
    .filter(char => char.tag !== 'to delete')
    .every(char => {
      // Vérifier selon le type de caractéristique
//      const config = characteristicTypeOptionsConfig[char.kind];
      const config = char.kind ? characteristicTypeOptionsConfig[char.kind] : null;  /////////////////////////////

	  
	  
      // Si le type nécessite un nom, vérifier qu'il est rempli
      if (config?.requiresName && (!char.name || char.name.trim() === '')) {
        return false;
      }
      
      // Toujours vérifier la valeur
      if (!char.value || char.value.trim() === '') {
        return false;
      }
      
      return true;
    });
};

  // ============================================================================
  // Gestion des médias/photos - VERSION COMPLÈTE AVEC THUMBNAILS
  // ============================================================================

  const fileInputRefsMap = useRef<Map<string, React.RefObject<HTMLInputElement>>>(new Map());

  const getOrCreateFileInputRef = (mediaId: string | number) => {
    const key = `${mediaId}`;
    if (!fileInputRefsMap.current.has(key)) {
      const newRef = React.createRef<HTMLInputElement>();
      fileInputRefsMap.current.set(key, newRef);
      console.log('✅ Nouvelle ref créée pour média variant:', key);
    }
    return fileInputRefsMap.current.get(key)!;
  };

  const handlePhotoReorder = (reorderedIds: string[]) => {
    console.log('🔄 handlePhotoReorder variant appelé avec:', reorderedIds);

    setFields(prev => {
      const mediaMap = new Map(fields.medias.map(media => [`${media.id}`, media]));
      
      const reorderedMedias = reorderedIds
        .map(id => mediaMap.get(id))
        .filter((media): media is ProductMedia => media !== undefined)
        .map((media, newIndex) => {
          const newRank = newIndex + 1;
          const hasRankChanged = media.rank !== newRank;
          
          return {
            ...media,
            rank: newRank,
            tag: hasRankChanged && media.tag !== 'to create' && media.tag !== 'to delete' 
              ? 'to update' 
              : media.tag
          };
        });

      console.log('📊 Nouveaux ranks et tags variant:', reorderedMedias.map(m => ({ 
        id: m.id, 
        rank: m.rank, 
        tag: m.tag 
      })));

      const updatedFields = { ...prev, medias: reorderedMedias };
      notifyVariantChange(updatedFields);
      return updatedFields;
    });
  };

  // Handler pour définir une image comme thumbnail
  const handleSetThumbnail = (targetMediaId: string | number) => {
    console.log('⭐ handleSetThumbnail variant appelé pour média:', targetMediaId);
    
    if (onSetThumbnail) {
      // Utiliser le handler du parent pour gérer globalement
      onSetThumbnail(targetMediaId);
    } else {
      // Fallback : gestion locale (comme avant)
      setFields((prev) => {
        const updatedMedias = prev.medias.map((media) => {
          if (`${media.id}` === `${targetMediaId}` && media.mediaType === 'Image') {
            const wasAlreadyThumbnail = media.asThumbnail;
            if (wasAlreadyThumbnail) return media;
            
            return {
              ...media,
              asThumbnail: true,
              tag: media.tag === 'to create' ? 'to create' : 'to update'
            };
          }
          else if (media.mediaType === 'Image' && media.asThumbnail) {
            return {
              ...media,
              asThumbnail: false,
              tag: media.tag === 'to create' ? 'to create' : 'to update'
            };
          }
          return media;
        });

        const updatedFields = { ...prev, medias: updatedMedias };
        notifyVariantChange(updatedFields);
        return updatedFields;
      });
    }
  };
    
	
/*	
  const handleAddPhotoFromCard = () => {
    const newPhotoId = `media_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    
    const currentMedias = fields.medias;
    const maxRank = currentMedias.length > 0 
      ? Math.max(...currentMedias.map(m => m.rank)) 
      : 0;
    const newRank = maxRank + 1;
    
    // Vérifier s'il y a déjà un thumbnail parmi les images
    const hasThumbnail = currentMedias.some(m => m.mediaType === 'Image' && m.asThumbnail);
    
    const newPhoto = {
      ...initializedMedia(),
      open: true,
      id: newPhotoId,
      rank: newRank,
      tag: 'to create',
      asThumbnail: !hasThumbnail, // ✅ Première image = thumbnail par défaut
      mediaType: 'Image' // ✅ On définit explicitement le type
    };

    console.log('➕ Ajout nouvelle photo variant, ID:', newPhotoId, 'Rank:', newRank, 'asThumbnail:', !hasThumbnail);

    setFields(prev => {
      const updatedFields = { ...prev, medias: [...prev.medias, newPhoto] };
      notifyVariantChange(updatedFields);
      return updatedFields;
    });

    setOpenMedias(prev => ({ ...prev, [fields.medias.length]: true }));

    const fileInputRef = getOrCreateFileInputRef(newPhotoId);
    console.log('➕ Ref créée pour nouvelle photo variant:', fileInputRef);
  };
*/

const handleAddPhotoFromCard = () => {
  // Vérifier si tous les médias existants sont valides
  if (!checkAllMediasValid()) {
    console.warn('⚠️ Impossible d\'ajouter un média variant : certains médias existants n\'ont pas de fichier');
    return; // Bloquer l'ajout
  }

  const newPhotoId = `media_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  
  const currentMedias = fields.medias;
  const maxRank = currentMedias.length > 0 
    ? Math.max(...currentMedias.map(m => m.rank)) 
    : 0;
  const newRank = maxRank + 1;
  
  const hasThumbnail = currentMedias.some(m => m.mediaType === 'Image' && m.asThumbnail);
  
  const newPhoto = {
    ...initializedMedia(),
    open: true,
    id: newPhotoId,
    rank: newRank,
    tag: 'to create',
    asThumbnail: !hasThumbnail,
    mediaType: 'Image'
  };

  console.log('➕ Ajout nouvelle photo variant, ID:', newPhotoId, 'Rank:', newRank, 'asThumbnail:', !hasThumbnail);

  setFields(prev => {
    const updatedFields = { ...prev, medias: [...prev.medias, newPhoto] };
    notifyVariantChange(updatedFields);
    return updatedFields;
  });

  setOpenMedias(prev => ({ ...prev, [fields.medias.length]: true }));

  const fileInputRef = getOrCreateFileInputRef(newPhotoId);
  console.log('➕ Ref créée pour nouvelle photo variant:', fileInputRef);
};

  const handlePhotoUpload = (itemId: string) => {
    console.log('🔵 handlePhotoUpload variant appelé pour itemId:', itemId);
    
    const fileInputRef = fileInputRefsMap.current.get(itemId);
    console.log('🔵 fileInputRef récupérée:', fileInputRef);
    console.log('🔵 fileInputRef?.current:', fileInputRef?.current);
    
    if (fileInputRef?.current) {
      console.log('✅ Click sur input file variant');
      fileInputRef.current.click();
    } else {
      console.error('❌ Aucune ref disponible pour itemId variant:', itemId);
    }
  };

  const handlePhotoLibrary = (itemId: string) => {
    console.log('📚 Ouvrir la bibliothèque pour le média variant:', itemId);
    // TODO: Implémenter l'ouverture de la bibliothèque de médias
  };

/*
  const renderPhotoField = () => {
    console.log('🎨 Rendering photos variant, count:', fields.medias.length);
    
    const visibleMedias = fields.medias
      .filter(media => media.tag !== 'to delete')
      .sort((a, b) => a.rank - b.rank);
    
    const cardItems: Item[] = visibleMedias.map((media, index) => {
      const mediaId = `${media.id}`;
      const fileInputRef = getOrCreateFileInputRef(mediaId);
      
      console.log(`📸 Photo variant ${index} - ID: ${mediaId}, Rank: ${media.rank}, Tag: ${media.tag}, asThumbnail: ${media.asThumbnail}, Ref:`, fileInputRef);
      
      return {
        id: mediaId,
        component: (
          <ProductSheet_Media 
            media={media}
            errorMessages={errorMessagesForMedias}
            indexInErrorMessages={index}
            title={`Photo n°${index + 1}`}
            onChange={(p) => handlePhotoChange(p, index)}
            onThumbnailClick={() => handleSetThumbnail(mediaId)}
            publicRef={publicRef}
            withCollapsiblePanel={false}
            externalMode={true}
            showTextInput={false}
            fileInputRef={fileInputRef}
            showMediaWithLabel={false}
            showThumbnailStar={false}
            thumbnailStarInComponent={false}
            showLegend={false}
          />
        ),
        isThumbnail: media.asThumbnail,
        hasContent: !!(media.publicFileName || media.fileContent),
        isImage: media.mediaType === 'Image'
      };
    });

    return (
      <CardManager 
        containerWidth="100%" 
        containerHeight="335px" 
        cardWidth={325} 
        cardHeight={300}
        gap={20}
        showRemove={true}
        showUpload={true}
        showLibrary={true}
        showThumbnail={true}
        onAdd={handleAddPhotoFromCard} 
        onRemove={handleDeletePhoto}
        onUpload={handlePhotoUpload}
        onLibrary={handlePhotoLibrary}
        onThumbnail={handleSetThumbnail}
        onReorder={handlePhotoReorder}
        items={cardItems}
        fileInputRefs={fileInputRefsMap.current}
      />
    );
  };
*/

const renderPhotoField = () => {
  console.log('🎨 Rendering photos variant, count:', fields.medias.length);
  
  const visibleMedias = fields.medias
    .filter(media => media.tag !== 'to delete')
    .sort((a, b) => a.rank - b.rank);
  
  const cardItems: Item[] = visibleMedias.map((media, index) => {
    const mediaId = `${media.id}`;
    const fileInputRef = getOrCreateFileInputRef(mediaId);
    
    // Vérifier si le média a une erreur (pas de fichier)
    const hasError = !(media.publicFileName || media.fileContent);
    
    console.log(`📸 Photo variant ${index} - ID: ${mediaId}, Rank: ${media.rank}, Tag: ${media.tag}, asThumbnail: ${media.asThumbnail}, hasError: ${hasError}`);
    
    return {
      id: mediaId,
      component: (
        <ProductSheet_Media 
          media={media}
          errorMessages={errorMessagesForMedias}
          indexInErrorMessages={index}
          title={`Photo n°${index + 1}`}
          onChange={(p) => handlePhotoChange(p, index)}
          onThumbnailClick={() => handleSetThumbnail(mediaId)}
          publicRef={publicRef}
          withCollapsiblePanel={false}
          externalMode={true}
          showTextInput={false}
          fileInputRef={fileInputRef}
          showMediaWithLabel={false}
          showThumbnailStar={false}
          thumbnailStarInComponent={false}
          showLegend={false}
          hasError={hasError} // ← AJOUTER CETTE PROP
        />
      ),
      isThumbnail: media.asThumbnail,
      hasContent: !!(media.publicFileName || media.fileContent),
      isImage: media.mediaType === 'Image'
    };
  });

  return (
    <CardManager 
      containerWidth="100%" 
      containerHeight="335px" 
      cardWidth={325} 
      cardHeight={300}
      gap={20}
      showRemove={true}
      showUpload={true}
      showLibrary={true}
      showThumbnail={true}
      onAdd={handleAddPhotoFromCard} 
      onRemove={handleDeletePhoto}
      onUpload={handlePhotoUpload}
      onLibrary={handlePhotoLibrary}
      onThumbnail={handleSetThumbnail}
      onReorder={handlePhotoReorder}
      items={cardItems}
      fileInputRefs={fileInputRefsMap.current}
    />
  );
};


  const handleDeletePhoto = (key: string) => {
    console.log('🗑️ handleDeletePhoto variant', key);

    setFields((prev) => {
      const updatedMedias = prev.medias.map((media) => {
        if (`${media.id}` === key) {
          // ✅ Si le média est nouveau ('to create'), on peut le supprimer vraiment
          if (media.tag === 'to create') {
            return null; // On le supprimera ensuite avec filter
          }
          // ✅ Sinon, on le marque comme 'to delete'
          return {
            ...media,
            tag: 'to delete'
          };
        }
        return media;
      }).filter((media): media is ProductMedia => media !== null);

      const updatedFields = { ...prev, medias: updatedMedias };
      notifyVariantChange(updatedFields);
      return updatedFields;
    });

    // Nettoyer la ref du média supprimé
    fileInputRefsMap.current.delete(key);
    console.log('🗑️ Média variant marqué pour suppression ou supprimé:', key);
  };

  const handlePhotoChange = (updatedPhoto: ProductMedia, photoIndex: number) => {
    console.log('📝 handlePhotoChange variant', updatedPhoto, photoIndex);

    setFields((prev) => {
      const updatedPhotos = [...prev.medias];
      const originalPhoto = updatedPhotos[photoIndex];
      
      const hasChanged = 
        originalPhoto.name !== updatedPhoto.name ||
        originalPhoto.fileContent !== updatedPhoto.fileContent;
      
      const newTag = hasChanged && originalPhoto.tag !== 'to create' && originalPhoto.tag !== 'to delete'
        ? 'to update'
        : originalPhoto.tag || updatedPhoto.tag;

      // ✅ S'assurer que le mediaType est 'Image' si c'est un nouvel upload
      updatedPhotos[photoIndex] = {
        ...updatedPhoto,
        tag: newTag,
        mediaType: updatedPhoto.mediaType || 'Image' // ✅ Défaut à 'Image'
      };
      
      const updatedFields = { ...prev, medias: updatedPhotos };
      
      console.log('📝 Photo variant modifiée, nouveau tag:', newTag, 'mediaType:', updatedPhotos[photoIndex].mediaType);
      
      notifyVariantChange(updatedFields);
      return updatedFields;
    });

    setErrorMessagesForMedias((prev) => {
      const updatedErrors = [...prev];
      const contentError = !updatedPhoto.fileContent && !updatedPhoto.publicFileName ? 'Il manque le fichier' : '';
      updatedErrors[photoIndex] = { content: contentError };
      return updatedErrors;
    });
  };
  
const checkAllMediasValid = (): boolean => {
  return fields.medias
    .filter(media => media.tag !== 'to delete')
    .every(media => !!(media.publicFileName || media.fileContent));
};  
  

  // ============================================================================
  // RENDER - avec conditions basées sur la config
  // ============================================================================

  const renderForm = () => (
    <div className={classNames(styles.contain)}>

      {/* CHAMPS CONFIGURABLES */}
      
      {fieldsConfig.showActive && (
        <div className={classNames(styles.active)}>
          <WidgetFrame name="Actif :" labelTextPosition="right" classNameGrid={classNames(styles.grid_frame)}>
            <Toggle checked={fields.active} onChange={handleActiveChange} form="switch" />
          </WidgetFrame>
        </div>
      )}

      {fieldsConfig.showBarcode && (
        <div className={classNames(styles.barcode)}>
          <WidgetFrame name="Code barre :" labelTextPosition="right" classNameGrid={classNames(styles.grid_frame)}>
            <Field type="text" value={fields.barcode} onChange={handleBarcodeChange} />
          </WidgetFrame>
        </div>
      )}

      {fieldsConfig.showVariantCode && (
        <div className={classNames(styles.code)}>
          <WidgetFrame name="Référence :" labelTextPosition="right" error={messages.code} classNameGrid={classNames(styles.grid_frame)}>
            <Field type="text" value={fields.code} onChange={handleCodeChange} />
          </WidgetFrame>
        </div>
      )}

      {fieldsConfig.showVariantName && (
        <div className={classNames(styles.name)}>
          <WidgetFrame name="Nom :" labelTextPosition="right" error={messages.name} classNameGrid={classNames(styles.grid_frame)}>
            <Field type="text" value={fields.name} onChange={handleNameChange} />
          </WidgetFrame>
        </div>
      )}

      {fieldsConfig.showComplement && (
        <div className={classNames(styles.complement)}>
          <WidgetFrame name="Complément :" labelTextPosition="right" classNameGrid={classNames(styles.grid_frame)}>
            <Field type="text" value={fields.complement} onChange={handleComplementChange} />
          </WidgetFrame>
        </div>
      )}

      {fieldsConfig.showDescription && (
        <div className={classNames(styles.description)}>
          <WidgetFrame name="Description :" labelTextPosition="right" classNameGrid={classNames(styles.grid_frame)}>
            <RichTextEditor value={fields.description} onChange={handleDescriptionChange} />
          </WidgetFrame>
        </div>
      )}

      {fieldsConfig.showStock && (
        <>
          {fieldsConfig.showStockAvailable && (
            <div className={classNames(styles.stockAvailable)}>
              <WidgetFrame name="Disponible :" labelTextPosition="right" classNameGrid={classNames(styles.grid_frame)}>
                <Field type="number" value={fields.stockAvailable} onChange={handleStockAvailableChange} />
              </WidgetFrame>
            </div>
          )}

          {fieldsConfig.showStockThreshold && (
            <div className={classNames(styles.stockThreshold)}>
              <WidgetFrame name="Alerte :" labelTextPosition="right" classNameGrid={classNames(styles.grid_frame)}>
                <Field type="number" value={fields.stockThreshold} onChange={handleStockThresholdChange} />
              </WidgetFrame>
            </div>
          )}

          {fieldsConfig.showStockBooked && isEditMode && (
            <div className={classNames(styles.stockBooked)}>
              <WidgetFrame name="Panier :" labelTextPosition="right" classNameGrid={classNames(styles.grid_frame)}>
                <Field type="number" value={fields.stockBooked} onChange={handleStockBookedChange} />
              </WidgetFrame>
            </div>
          )}

          {fieldsConfig.showStockAssigned && isEditMode && (
            <div className={classNames(styles.stockAssigned)}>
              <WidgetFrame name="A envoyer :" labelTextPosition="right" classNameGrid={classNames(styles.grid_frame)}>
                <Field type="number" value={fields.stockAssigned} onChange={handleStockAssignedChange} />
              </WidgetFrame>
            </div>
          )}
        </>
      )}

      {fieldsConfig.showCharacteristics && (
        <div className={classNames(styles.characteristics)}>
          <WidgetFrame name="Caractéristiques :" labelTextPosition="right" classNameGrid={classNames(styles.grid_frame)}>
            {renderCharacteristicField()}
          </WidgetFrame>
        </div>
      )}

      {fieldsConfig.showPhotos && (
        <div className={classNames(styles.photos)}>
          <WidgetFrame name="Photos :" labelTextPosition="right" classNameGrid={classNames(styles.grid_frame)}>
            {renderPhotoField()}
          </WidgetFrame>
        </div>
      )}

      {/* ✅ CHANGEMENT 4: Utilisation de price.id comme key dans le map */}
      {fieldsConfig.showPrices && (
        <div className={classNames(styles.prices)}>
          <WidgetFrame name="Prix" labelTextPosition="right" classNameGrid={classNames(styles.grid_frame)}>
            <div className={classNames(styles.bloc)}>
              {fields.prices.map((price, index) => (
                <div className={classNames(styles.panelWrapper)} key={price.id}>
                  <ProductSheet_Price price={price}
                                      title={`Prix ${index + 1}`}
                                      onChange={(p) => handlePriceChange(p, index)}
                                      onDelete={() => handleDeletePrice(index)}
                                      open={openPrices[index] ?? undefined} />
                </div>
              ))}
              <Trigger name="Ajouter un Prix" onClick={handleAddPrice} variant="tailwind2" />
            </div>
          </WidgetFrame>
        </div>
      )}

      {/* Palier récurrent (abonnement) : pas édité ici (recurringIntervalUnit se pose en
          base pour l'instant), seulement affiché — et le plan Paypal, lui, se crée
          immédiatement au clic, indépendamment du bouton Enregistrer de la fiche. */}
      {initialProductVariant.recurringIntervalUnit && (
        <div className={classNames(styles.recurring)}>
          <WidgetFrame name="Abonnement :" labelTextPosition="right" classNameGrid={classNames(styles.grid_frame)}>
            <div className={classNames(styles.bloc)}>
              <div>Récurrence : {initialProductVariant.recurringIntervalCount ?? 1} × {initialProductVariant.recurringIntervalUnit}</div>
              {
                externalPaypalPlanId
                ? <div>Plan Paypal : {externalPaypalPlanId}</div>
                : (
                  <>
                    <Trigger
                      name={creatingPaypalPlan ? "Création..." : "Créer le plan Paypal"}
                      onClick={handleCreatePaypalPlan}
                      variant="tailwind2"
                    />
                    {paypalPlanError && <div className={classNames(styles.error)}>{paypalPlanError}</div>}
                  </>
                )
              }
            </div>
          </WidgetFrame>
        </div>
      )}
    </div>
  );

  return (
    <div className={classNames(styles.root)}>
      <CollapsiblePanel title={title} onDelete={onDelete} open={open}>
        {renderForm()}
      </CollapsiblePanel>
    </div>
  );
};

export default ProductSheet_ProductVariant;